import { GlobalSettingsControl } from "@/components/common/GlobalSettingsControl";
import { OperatingAsBanner } from "@/components/common/OperatingAsBanner";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { AdminAccordionSection } from "./AdminAccordionSection";
import { AdminContactsDirectorySection } from "./AdminContactsDirectorySection";
import { OrgSettingsSection } from "./OrgSettingsSection";
import { OrgHeadquartersSection } from "./OrgHeadquartersSection";
import { GhlSection } from "./GhlSection";
import { OrgDeactivateSection } from "./OrgDeactivateSection";
import { OrgLocationsSection } from "./OrgLocationsSection";
import { AdminPanelUsersModal } from "./AdminPanelUsersModal";
import { AdminActivityTimelineSection } from "./AdminActivityTimelineSection";
import { MfgQcEmailSection } from "./MfgQcEmailSection";

function adminRoleLabel(role: string | undefined): string {
  if (role === "platform_owner") return "Platform Owner";
  if (role === "platform_admin") return "Platform Admin";
  if (role === "platform_dev" || role === "dev") return "Platform Dev";
  if (role === "platform_trainer") return "Platform Trainer";
  if (role === "owner" || role === "org_owner") return "Org Owner";
  if (role === "admin" || role === "org_admin") return "Org Admin";
  return "User";
}

function adminModeCardClass(active: boolean, pendingPulse?: boolean): string {
  if (active) {
    return (
      "relative rounded-xl border border-indigo-500 bg-indigo-50/70 p-4 pr-20 text-left " +
      "ring-2 ring-indigo-200 dark:border-indigo-500 dark:bg-indigo-950/35 dark:ring-indigo-800/70"
    );
  }
  if (pendingPulse) {
    return (
      "relative rounded-xl border border-amber-400 p-4 pr-4 text-left animate-admin-pending-pulse " +
      "motion-reduce:animate-none dark:border-amber-500/90 dark:animate-admin-pending-pulse-dark hover:brightness-[1.02]"
    );
  }
  return (
    "relative rounded-xl border border-gray-200 bg-white p-4 text-left hover:bg-gray-50 " +
    "dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800/80"
  );
}

export function AdminPanelContent(props: any) {
  // Container/padding doctrine \u00a7K1+K3+K6: embedded mode (the in-shell variant
  // mounted by LeadWorkspaceShellModes) renders flat \u2014 no extra `p-6` ring, no
  // hardcoded width cap. The shell owns padding. The standalone path keeps its
  // outer `min-h-screen` for when /admin is opened directly.
  // See docs/planning/container_padding_pass.md \u00a7K.
  const outer = props.embedded
    ? "w-full text-left space-y-4"
    : "min-h-screen bg-gray-50 dark:bg-gray-950 px-4 py-4 sm:px-6 lg:px-8 lg:py-6";
  return (
    <div className={outer} {...{ [FIREFLY_TARGET_ATTR]: "admin-surface" }}>
      <OperatingAsBanner
        roleLabel={adminRoleLabel(props.auth.role)}
        orgName={props.displayOrgName}
        rightSlot={props.orgOptions.length > 0 ? (
          <select className="text-xs border border-amber-300 rounded-lg px-2 py-1 bg-white dark:border-amber-800/80 dark:bg-zinc-900 dark:text-zinc-100" value={props.effectiveOrgId ?? ""} onChange={(e) => props.onOrgChange(e.target.value)}>
            {props.orgOptions.map((o: any) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        ) : undefined}
      />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Admin</h1>
          <div className="text-xs text-gray-400 font-mono mt-0.5">Workspace org: {props.effectiveOrgId ?? "\u2014"}{props.activeOrgId && props.activeOrgId !== props.effectiveOrgId ? <span className="block text-[10px] text-gray-500">Profile primary: {props.activeOrgId}</span> : null}</div>
        </div>
        <div className="flex items-center gap-2">
          {!props.embedded ? <GlobalSettingsControl variant="chip" /> : null}
          <button type="button" onClick={() => void props.refresh()} className="px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800" disabled={props.loading}>Refresh</button>
        </div>
      </div>
      {props.msg && <div className="p-3 rounded-lg bg-white border border-gray-200 text-sm text-gray-800 dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-100">{props.msg}</div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className={adminModeCardClass(props.activeModal === "users", props.hasUserPending)}>
          {props.activeModal === "users" ? (
            <button
              type="button"
              onClick={() => void props.refresh()}
              className="absolute top-3 right-3 px-2 py-1 rounded-lg border border-indigo-300 bg-white text-xs text-indigo-800 hover:bg-indigo-50 dark:border-indigo-700 dark:bg-zinc-900 dark:text-indigo-200 dark:hover:bg-indigo-950/50"
              disabled={props.loading}
            >
              Refresh
            </button>
          ) : null}
          <button
            type="button"
            {...{ [FIREFLY_TARGET_ATTR]: "admin-users-card" }}
            onClick={() => props.setActiveModal((prev: any) => (prev === "users" ? null : "users"))}
            className="w-full text-left"
          >
            <div className={"font-semibold " + (props.hasUserPending ? "text-gray-900 dark:text-amber-50" : "text-gray-900 dark:text-gray-100")}>
              App User Admin
            </div>
            <div className={"text-xs mt-1 " + (props.hasUserPending ? "text-gray-700 dark:text-amber-200/90" : "text-gray-600 dark:text-gray-400")}>
              Pending approvals, role assignment, active members, invite user
            </div>
          </button>
        </div>
        <div className={adminModeCardClass(props.activeModal === "contacts")}>
          {props.activeModal === "contacts" ? (
            <button
              type="button"
              onClick={() => void props.refresh()}
              className="absolute top-3 right-3 px-2 py-1 rounded-lg border border-indigo-300 bg-white text-xs text-indigo-800 hover:bg-indigo-50 dark:border-indigo-700 dark:bg-zinc-900 dark:text-indigo-200 dark:hover:bg-indigo-950/50"
              disabled={props.loading}
            >
              Refresh
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => props.setActiveModal((prev: any) => (prev === "contacts" ? null : "contacts"))}
            className="w-full text-left"
          >
            <div className="font-semibold text-gray-900 dark:text-zinc-100">Contacts Directory</div>
            <div className="text-xs text-gray-600 mt-1 dark:text-zinc-400">
              External/user directory, routing tags, import, invite-link and migrate
            </div>
          </button>
        </div>
        <div className={adminModeCardClass(props.activeModal === "org")}>
          {props.activeModal === "org" ? (
            <button
              type="button"
              onClick={() => void props.refresh()}
              className="absolute top-3 right-3 px-2 py-1 rounded-lg border border-indigo-300 bg-white text-xs text-indigo-800 hover:bg-indigo-50 dark:border-indigo-700 dark:bg-zinc-900 dark:text-indigo-200 dark:hover:bg-indigo-950/50"
              disabled={props.loading}
            >
              Refresh
            </button>
          ) : null}
          <button
            type="button"
            {...{ [FIREFLY_TARGET_ATTR]: "admin-org-settings" }}
            onClick={() => props.setActiveModal((prev: any) => (prev === "org" ? null : "org"))}
            className="w-full text-left"
          >
            <div className="font-semibold text-gray-900 dark:text-zinc-100">Org Administration</div>
            <div className="text-xs text-gray-600 mt-1 dark:text-zinc-400">
              Organization settings, CRM connections, operational notes
            </div>
          </button>
        </div>
      </div>
      {props.activeModal ? (
        <div className="space-y-3 pt-1">
          {props.activeModal === "users" ? <AdminPanelUsersModal {...props} /> : null}
          {props.activeModal === "contacts" ? (props.effectiveOrgId ? <AdminContactsDirectorySection organizationId={props.effectiveOrgId} onToast={(m, v) => props.setMsg(v === "err" ? `Error: ${m}` : m)} /> : <p className="text-sm text-gray-500">Select a workspace organization first.</p>) : null}
          {props.activeModal === "org" ? (
            <>
              <AdminAccordionSection
                title="Branding & identity"
                open={props.open.has("orgbranding")}
                plain
                onToggle={() => props.toggle("orgbranding")}
              >
                {props.effectiveOrgId ? (
                  <OrgSettingsSection organizationId={props.effectiveOrgId} plain group="branding" />
                ) : null}
              </AdminAccordionSection>
              <AdminAccordionSection
                title="Headquarters settings"
                open={props.open.has("orghq")}
                plain
                onToggle={() => props.toggle("orghq")}
              >
                {props.effectiveOrgId ? (
                  <OrgHeadquartersSection organizationId={props.effectiveOrgId} />
                ) : (
                  <p className="text-sm text-gray-500">Select a workspace organization first.</p>
                )}
              </AdminAccordionSection>
              <AdminAccordionSection
                title="Sales & proposals"
                open={props.open.has("orgsales")}
                plain
                onToggle={() => props.toggle("orgsales")}
              >
                {props.effectiveOrgId ? (
                  <OrgSettingsSection organizationId={props.effectiveOrgId} plain group="sales" />
                ) : null}
              </AdminAccordionSection>
              <AdminAccordionSection
                title="Product suites"
                open={props.open.has("orgsuites")}
                plain
                onToggle={() => props.toggle("orgsuites")}
              >
                {props.effectiveOrgId ? (
                  <OrgSettingsSection organizationId={props.effectiveOrgId} plain group="product_suites" />
                ) : null}
              </AdminAccordionSection>
              <AdminAccordionSection
                title="Email Events"
                open={props.open.has("emailevents")}
                plain
                onToggle={() => props.toggle("emailevents")}
              >
                {props.effectiveOrgId ? (
                  <MfgQcEmailSection />
                ) : (
                  <p className="text-sm text-gray-500">Select a workspace organization first.</p>
                )}
              </AdminAccordionSection>
              <AdminAccordionSection title="GHL CRM Connections" open={props.open.has("ghl")} plain onToggle={() => props.toggle("ghl")}>{props.effectiveOrgId && <GhlSection organizationId={props.effectiveOrgId} orgName={props.displayOrgName ?? undefined} plain />}</AdminAccordionSection>
              <AdminAccordionSection title="Locations" open={props.open.has("locations")} plain onToggle={() => props.toggle("locations")} headerFireflyTargetId="admin-locations">{props.effectiveOrgId ? <OrgLocationsSection organizationId={props.effectiveOrgId} plain /> : <p className="text-sm text-gray-500">Select a workspace organization first.</p>}</AdminAccordionSection>
              <AdminAccordionSection title="Activity timeline" open={props.open.has("activitytimeline")} plain onToggle={() => props.toggle("activitytimeline")}>
                {props.effectiveOrgId ? (
                  <AdminActivityTimelineSection
                    organizationId={props.effectiveOrgId}
                    enabled={props.open.has("activitytimeline")}
                    members={props.activeMembers}
                    auth={props.auth}
                    plain
                  />
                ) : (
                  <p className="px-4 py-3 text-sm text-gray-500">Select a workspace organization first.</p>
                )}
              </AdminAccordionSection>
              <AdminAccordionSection title="Organization lifecycle" open={props.open.has("orglifecycle")} plain onToggle={() => props.toggle("orglifecycle")}>
                {props.effectiveOrgId ? <OrgDeactivateSection organizationId={props.effectiveOrgId} orgName={props.activeOrgName} activeMemberCount={props.activeMembers.length} isPlatformAdminOrDev={props.auth.realHasRole("platform_admin")} onDeactivated={() => void props.refresh()} /> : <p className="text-sm text-gray-500">Select a workspace organization first.</p>}
              </AdminAccordionSection>
              <AdminAccordionSection title="Notes" open={props.open.has("notes")} plain onToggle={() => props.toggle("notes")}>
                <div className="text-sm text-gray-600 space-y-1 px-1">
                  <div>
                    RPCs used: {(["admin_approve_profile", "admin_assign_membership", "admin_block_profile", "admin_list_org_members", "admin_list_members_with_profiles", "admin_deactivate_membership", "admin_add_org_role", "admin_remove_org_role", "admin_list_member_locations", "admin_add_user_location", "admin_remove_user_location", "admin_list_member_plants", "admin_add_user_plant", "admin_remove_user_plant", "admin_list_org_plants", "admin_list_member_capabilities", "admin_set_member_capability"] as const).map((fn, i, arr) => <span key={fn}><span className="font-mono">{fn}</span>{i < arr.length - 1 ? ", " : ""}</span>)}
                  </div>
                  <div>If you see permission errors, run <span className="font-mono">docs/supabase_admin_onboarding.sql</span> in Supabase.</div>
                </div>
              </AdminAccordionSection>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
